import {
  cacheKeys,
  FIND_GROUP_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import {
  Colors,
  LoadingIndicator,
  SaveFormTop,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import '../../shared/style/reactTabStyle.css';
import { EditGroupAccountsList } from './editGroupAccountsList';
import { EditGroupAssetsList } from './editGroupAssetsList';
import { EditGroupRoutinesList } from './editGroupRoutinesList';
import { GroupAssignmentSearchInput } from './groupAssignmentSearchInput';

const ASSIGN_ASSETS_TO_GROUP = `
  mutation AssignAssetsToGroup($input: AssignToGroupInput!) {
    group {
      assignToGroup(input: $input) {
        success
        message
      }
    }
  }
`;

const ASSIGN_ACCOUNTS_TO_GROUP = `
  mutation AssignAccountsToGroup($input: AssignToGroupInput!) {
    group {
      assignToGroup(input: $input) {
        success
        message
      }
    }
  }
`;

const ASSIGN_ROUTINES_TO_GROUP = `
  mutation AssignRoutinesToGroup($input: AssignToGroupInput!) {
    group {
      assignToGroup(input: $input) {
        success
        message
      }
    }
  }
`;

export enum AssignmentType {
  Routines = 'routines',
  Assets = 'assets',
  People = 'people',
}

type GroupAssignmentFormProps = {
  groupId: string;
  title: string;
  assignmentType: AssignmentType;
  onComplete: () => void;
};

export const GroupAssignmentForm = ({
  title,
  onComplete,
  groupId,
  assignmentType,
}: GroupAssignmentFormProps) => {
  const [originalAssetAssignments, setOriginalAssetAssignments] = useState<
    string[] | undefined
  >();

  const [originalAccountAssignments, setOriginalAccountAssignments] = useState<
    string[] | undefined
  >();

  const [originalRoutineAssignments, setOriginalRoutineAssignments] = useState<
    string[] | undefined
  >();

  const [assetAssignments, setAssetAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [accountAssignments, setAccountAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [routineAssignments, setRoutineAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [keyword, setKeyword] = useState('');

  const [tabIndex, setTabIndex] = useState(0);
  const [assignmentMode, setAssignmentMode] = useState<AssignmentType>(
    AssignmentType.Routines
  );

  const tabIndexAssignment = useMemo(
    () => [
      AssignmentType.Assets,
      AssignmentType.People,
      AssignmentType.Routines,
    ],
    []
  );

  const isAssets = assignmentMode === AssignmentType.Assets;
  const isAccounts = assignmentMode === AssignmentType.People;
  const isRoutines = assignmentMode === AssignmentType.Routines;

  const { t } = useTranslation();

  const { data: assignments, isPending: isAssignmentsLoading } = useCustomQuery(
    {
      query: FIND_GROUP_ASSIGNMENTS,
      cacheKey: ['group', 'assignments', groupId],
      variables: {
        groupId: groupId,
      },
    }
  );

  const { mutateAsync: assignAssetsToGroup, isPending: isAssetsLoading } =
    useCustomMutation({
      query: ASSIGN_ASSETS_TO_GROUP,
    }) as any;

  const { mutateAsync: assignAccountsToGroup, isPending: isAccountsLoading } =
    useCustomMutation({
      query: ASSIGN_ACCOUNTS_TO_GROUP,
    }) as any;

  const { mutateAsync: assignRoutinesToGroup, isPending: isRoutinesLoading } =
    useCustomMutation({
      query: ASSIGN_ROUTINES_TO_GROUP,
    }) as any;

  useEffect(() => {
    setKeyword('');
  }, [assignmentType, assignmentMode, tabIndex]);

  useEffect(() => {
    if (assignments && !isAssignmentsLoading) {
      const assetAssignments = assignments.group.findGroupAssignments.assets;
      setOriginalAssetAssignments(assetAssignments);
      setAssetAssignments(assetAssignments);

      const accounts = assignments.group.findGroupAssignments.accounts;
      setOriginalAccountAssignments(accounts);
      setAccountAssignments(accounts);

      const routines = assignments.group.findGroupAssignments.routines;
      setOriginalRoutineAssignments(routines);
      setRoutineAssignments(routines);
    }
  }, [assignments, isAssignmentsLoading]);

  useEffect(() => {
    let assignmentToIndex = tabIndexAssignment.indexOf(assignmentType);

    if (assignmentToIndex === -1) {
      assignmentToIndex = 0;
    }

    setTabIndex(assignmentToIndex);
    setAssignmentMode(assignmentType);
  }, [assignmentType, tabIndexAssignment]);

  const currentWorkspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();

  const height = dimensions?.height ?? 0;
  const tableResultsBlockHeight = 50;
  const listMaxHeight = height - tableResultsBlockHeight;

  const handleAssetsSubmit = async () => {
    const input = {
      assetIds: assetAssignments,
      groupId: groupId,
      workspaceId: currentWorkspace.id,
    };

    await assignAssetsToGroup(
      { input: input },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('group').all,
          });
        },
      }
    );

    onComplete();
  };

  const handleAccountsSubmit = async () => {
    const input = {
      accountIds: accountAssignments,
      groupId: groupId,
      workspaceId: currentWorkspace.id,
    };

    await assignAccountsToGroup(
      { input: input },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('group').all,
          });
        },
      }
    );

    onComplete();
  };

  const handleRoutinesSubmit = async () => {
    const input = {
      routineIds: routineAssignments?.map((id) =>
        typeof id === 'string' ? id : id.toString()
      ),
      groupId: typeof groupId === 'string' ? groupId : groupId.toString(),
      workspaceId:
        typeof currentWorkspace.id === 'string'
          ? currentWorkspace.id
          : currentWorkspace.id.toString(),
    };

    await assignRoutinesToGroup(
      { input: input },
      {
        onSuccess: (data: any) => {
          // Success handled by onComplete callback
        },
        onError: (error: any) => {
          console.error('Routine assignment error:', error);
        },
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('group').all,
          });
        },
      }
    );

    onComplete();
  };

  const isDirty = () => {
    if (isRoutines) {
      return !isEqual(originalRoutineAssignments, routineAssignments);
    } else if (isAssets) {
      return !isEqual(originalAssetAssignments, assetAssignments);
    } else if (isAccounts) {
      return !isEqual(originalAccountAssignments, accountAssignments);
    }
    return false;
  };

  return (
    <SaveFormTop
      onSubmit={
        isRoutines
          ? handleRoutinesSubmit
          : isAssets
          ? handleAssetsSubmit
          : handleAccountsSubmit
      }
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      {(isRoutines && isRoutinesLoading) ||
      (isAssets && isAssetsLoading) ||
      (isAccounts && isAccountsLoading) ||
      isAssignmentsLoading ? (
        <LoadingIndicator flexContainer />
      ) : (
        <View style={styles.container}>
          <GroupAssignmentSearchInput
            keyword={keyword}
            onChange={setKeyword}
            placeholder={
              isRoutines
                ? 'routineSearchPlaceholder'
                : isAssets
                ? 'assetSearchPlaceholder'
                : 'accountSearchPlaceholder'
            }
          />

          <View style={[styles.assignmentContainer]} onLayout={onLayout}>
            <Tabs
              selectedIndex={tabIndex}
              onSelect={(index) => {
                setTabIndex(index);
                setAssignmentMode(tabIndexAssignment[index]);
              }}
            >
              <TabList>
                <Tab>{t('groups.assignments.assets')}</Tab>
                <Tab>{t('groups.assignments.people')}</Tab>
                <Tab>{t('groups.assignments.routines')}</Tab>
              </TabList>

              <View style={styles.listContainer}>
                <TabPanel>
                  {assetAssignments && currentWorkspace?.id && (
                    <EditGroupAssetsList
                      workspace={currentWorkspace}
                      groupId={groupId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      assignments={assetAssignments}
                      updateAssignments={setAssetAssignments}
                    />
                  )}
                </TabPanel>
                <TabPanel>
                  {accountAssignments && currentWorkspace?.id && (
                    <EditGroupAccountsList
                      workspace={currentWorkspace}
                      groupId={groupId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      assignments={accountAssignments}
                      updateAssignments={setAccountAssignments}
                    />
                  )}
                </TabPanel>
                <TabPanel>
                  {routineAssignments && currentWorkspace?.id && (
                    <EditGroupRoutinesList
                      workspace={currentWorkspace}
                      groupId={groupId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      assignments={routineAssignments}
                      updateAssignments={setRoutineAssignments}
                    />
                  )}
                </TabPanel>
              </View>
            </Tabs>
          </View>
        </View>
      )}
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
  inputStyle: {
    backgroundColor: Colors.white,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    borderRadius: 8,
  },
});
